import { createBrowserRouter, Navigate, useParams } from 'react-router';
import { AppLayout, HomeRedirect, RequireAuth } from './components/Layout.tsx';
import { LoginPage } from './pages/auth/LoginPage.tsx';
import { RegisterPage } from './pages/auth/RegisterPage.tsx';
import { UsersPage } from './pages/users/UsersPage.tsx';
import { UserLayout } from './pages/users/UserLayout.tsx';
import { UserViewPage } from './pages/users/UserViewPage.tsx';
import { UserEditPage } from './pages/users/UserEditPage.tsx';
import { FixedOhcwPage } from './pages/users/FixedOhcwPage.tsx';
import { FixedBandModePage } from './pages/users/FixedBandModePage.tsx';
import { FixedMarathonPage } from './pages/users/FixedMarathonPage.tsx';
import { UserQsosPage } from './pages/users/UserQsosPage.tsx';
import { MatrixPage } from './pages/users/MatrixPage.tsx';
import { ImportSinglePage } from './pages/import/ImportSinglePage.tsx';
import { ImportAdifPage } from './pages/import/ImportAdifPage.tsx';
import { ReportPage } from './pages/reports/ReportPage.tsx';
import { ReportSearchPage } from './pages/reports/ReportSearchPage.tsx';
import { CompetitionsPage } from './pages/competitions/CompetitionsPage.tsx';
import { CompetitionPage } from './pages/competitions/CompetitionPage.tsx';
import { CompetitionEditPage } from './pages/competitions/CompetitionEditPage.tsx';
import { ParticipatePage } from './pages/competitions/ParticipatePage.tsx';
import { CountriesPage } from './pages/admin/CountriesPage.tsx';
import { CountryEditPage } from './pages/admin/CountryEditPage.tsx';
import { BandsModesPage } from './pages/admin/BandsModesPage.tsx';
import { HelpAdminPage } from './pages/admin/HelpAdminPage.tsx';
import { SettingsPage } from './pages/admin/SettingsPage.tsx';
import { NotFound } from './pages/NotFound.tsx';

/** Vanhan sovelluksen osoitteet (esim. /users/view/OH2BU) ohjataan uusiin */
function LegacyRedirect({ to }: { to: (p: Record<string, string | undefined>) => string }) {
  const params = useParams();
  return <Navigate to={to(params)} replace />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <HomeRedirect /> },

      // julkiset
      { path: 'reports/:id', element: <ReportPage /> },
      { path: 'competitions', element: <CompetitionsPage /> },
      { path: 'competitions/:id', element: <CompetitionPage /> },
      {
        path: 'users/:id',
        element: <UserLayout />,
        children: [
          { index: true, element: <UserViewPage /> },
          {
            element: <RequireAuth />,
            children: [
              { path: 'edit', element: <UserEditPage /> },
              { path: 'fixed/ohcw', element: <FixedOhcwPage /> },
              { path: 'fixed/band-mode', element: <FixedBandModePage /> },
              { path: 'fixed/marathon', element: <FixedMarathonPage /> },
              { path: 'qsos', element: <UserQsosPage /> },
              { path: 'matrix', element: <MatrixPage /> },
            ],
          },
        ],
      },

      {
        element: <RequireAuth />,
        children: [
          { path: 'users', element: <UsersPage /> },
          { path: 'import/single', element: <ImportSinglePage /> },
          { path: 'import/adif', element: <ImportAdifPage /> },
          { path: 'reports/search', element: <ReportSearchPage /> },
          { path: 'competitions/:id/participate', element: <ParticipatePage /> },
        ],
      },

      {
        element: <RequireAuth admin />,
        children: [
          { path: 'users/new', element: <UserEditPage create /> },
          { path: 'competitions/new', element: <CompetitionEditPage /> },
          { path: 'competitions/:id/edit', element: <CompetitionEditPage /> },
          { path: 'admin/countries', element: <CountriesPage /> },
          { path: 'admin/countries/new', element: <CountryEditPage /> },
          { path: 'admin/countries/:id', element: <CountryEditPage /> },
          { path: 'admin/bands', element: <BandsModesPage /> },
          { path: 'admin/help', element: <HelpAdminPage /> },
          { path: 'admin/settings', element: <SettingsPage /> },
        ],
      },

      // vanhat osoitteet
      { path: 'users/view/:id', element: <LegacyRedirect to={(p) => `/users/${p.id}`} /> },
      { path: 'users/edit/:id', element: <LegacyRedirect to={(p) => `/users/${p.id}/edit`} /> },
      { path: 'users/qso/:id', element: <LegacyRedirect to={(p) => `/users/${p.id}/qsos`} /> },
      { path: 'users/matrix/:id', element: <LegacyRedirect to={(p) => `/users/${p.id}/matrix`} /> },
      { path: 'users/ohcw/:id', element: <LegacyRedirect to={(p) => `/users/${p.id}/fixed/ohcw`} /> },
      { path: 'users/fixed/:id', element: <LegacyRedirect to={(p) => `/users/${p.id}/fixed/band-mode`} /> },
      { path: 'users/marathon/:id', element: <LegacyRedirect to={(p) => `/users/${p.id}/fixed/marathon`} /> },
      { path: 'reports/get/:id', element: <LegacyRedirect to={(p) => `/reports/${p.id}`} /> },
      { path: 'competitions/results/:id', element: <LegacyRedirect to={(p) => `/competitions/${p.id}`} /> },
      { path: 'contest/results/:id', element: <LegacyRedirect to={(p) => `/competitions/${p.id}`} /> },
      { path: 'competitions/list', element: <Navigate to="/competitions" replace /> },
      { path: 'import/mass', element: <Navigate to="/import/adif" replace /> },
      { path: 'logout', element: <Navigate to="/login" replace /> },

      { path: '*', element: <NotFound /> },
    ],
  },
]);
